import axios from 'axios';

// Tipos para as contas
export interface ContaType {
  id: string;
  nome: string;
  saldo: number;
  createdAt: string;
  updatedAt: string;
}

export interface CriarContaDto {
  nome: string;
}

export interface AlterarContaDto {
  nome: string;
}

export interface TransferirDto {
  contaOrigemId: string;
  contaDestinoId: string;
  valor: number;
  descricao?: string;
}

export interface SacarDto {
  contaId: string;
  valor: number;
  descricao?: string;
}

export interface DepositarDto {
  contaId: string;
  valor: number;
  descricao?: string;
}

export interface MovimentoExtratoType {
  id: number;
  data: string;
  tipo: number | 'Entrada' | 'Saida' | 'Transferencia';
  valor: number;
  descricao: string;
  contaOrigemNome?: string;
  contaDestinoNome?: string;
  tipoReferencia?: string;
  referenciaId?: number;
  usuarioNome?: string;
  saldoAposMovimento: number;
}

// Tipo para resultado paginado (compatível com backend)
export interface PageResult<T> {
  data: T[];
  total: number;
  pageIndex: number;
  pageSize: number;
  isSuccess: boolean;
  message: string;
  meta?: { [key: string]: any };
}

export interface ApiResponse<T> {
  data: T;
  isSuccess: boolean;
  message?: string;
}

// Service para comunicação com a API
export const contaService = {
  /**
   * Obtém todas as contas
   */
  async obterContas(): Promise<ContaType[]> {
    const response = await axios.get<ContaType[]>('/api/conta');
    return response.data;
  },

  /**
   * Obtém uma conta específica
   */
  async obterConta(id: string): Promise<ContaType> {
    const response = await axios.get<ContaType>(`/api/conta/${id}`);
    return response.data;
  },

  /**
   * Cria uma nova conta
   */
  async criarConta(dto: CriarContaDto): Promise<ApiResponse<ContaType>> {
    const response = await axios.post<ApiResponse<ContaType>>('/api/conta', dto);
    return response.data;
  },

  /**
   * Altera uma conta existente
   */
  async alterarConta(id: string, dto: AlterarContaDto): Promise<ApiResponse<ContaType>> {
    const response = await axios.put<ApiResponse<ContaType>>(`/api/conta/${id}`, dto);
    return response.data;
  },

  /**
   * Exclui uma conta
   */
  async excluirConta(id: string): Promise<ApiResponse<void>> {
    const response = await axios.delete<ApiResponse<void>>(`/api/conta/${id}`);
    return response.data;
  },

  /**
   * Obtém o extrato de uma conta
   */
  async obterExtrato(contaId: string, pageSize: number = 20, pageIndex: number = 0): Promise<PageResult<MovimentoExtratoType>> {
    const response = await axios.get<PageResult<MovimentoExtratoType>>(`/api/conta/${contaId}/extrato`, {
      params: { pageSize, pageIndex }
    });
    return response.data;
  },

  /**
   * Realiza transferência entre contas
   */
  async transferir(dto: TransferirDto): Promise<void> {
    const response = await axios.post<void>(`/api/conta/${dto.contaOrigemId}/transferir`, dto);
    return response.data;
  },

  /**
   * Realiza saque de uma conta
   */
  async sacar(dto: SacarDto): Promise<void> {
    const response = await axios.post<void>(`/api/conta/${dto.contaId}/sacar`, dto);
    return response.data;
  },

  /**
   * Realiza depósito em uma conta
   */
  async depositar(dto: DepositarDto): Promise<void> {
    const response = await axios.post<void>(`/api/conta/${dto.contaId}/depositar`, dto);
    return response.data;
  }
}; 